// ============================================================================
// Formatter resmi ETAMSIOS — SATU-SATUNYA sumber format angka/tanggal/rupiah
// di frontend (STANDAR_UI v1.1 A6, diperbaiki #4b-1 Keputusan Q64/Q66).
//
// Format wajib (A6):
//   Rupiah        : Rp1.250.000        (tanpa spasi, titik ribuan, tanpa desimal)
//   Tanggal       : 05 Agu 2026        (2 digit, bulan singkat)
//   Tanggal + jam : 05 Agu 2026 14.30 WITA  (jam 24 pakai TITIK + zona waktu)
//   Jumlah barang : 150 pcs · 12,5 m   (pemisah ribuan id-ID + satuan)
//   Persentase    : 80% · 10,5%        (tanpa desimal kecuali perlu)
//
// ZONA WAKTU — aturan S14, sistem TIDAK PERNAH menebak:
//   - `tzName` (IANA, mis. "Asia/Makassar") berasal dari data master
//     stores.timezone lewat lib/timezone.js — bukan dari zona perangkat.
//   - Dengan tzName: jam dirender di zona itu + label (WIB/WITA/WIT/UTC±).
//   - TANPA tzName (entitas tidak punya region/store yang bisa di-resolve,
//     atau stores.timezone kosong): jam dirender di zona perangkat TANPA
//     label — keputusan Owner Q67: jangan jatuh ke WIB, jangan beri label
//     hasil tebakan.
// DILARANG membuat formatter lokal di halaman (fmtIDR/toLocaleString inline).
// ============================================================================

const MONTHS_ID_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "Mei", "Jun",
  "Jul", "Agu", "Sep", "Okt", "Nov", "Des",
];

const TZ_LABELS = {
  "Asia/Jakarta": "WIB",
  "Asia/Makassar": "WITA",
  "Asia/Jayapura": "WIT",
};

export function formatIDR(amount) {
  if (amount === null || amount === undefined || amount === "") return "—";
  const n = Number(amount);
  if (Number.isNaN(n)) return "—";
  // A6: "Rp" menempel, titik ribuan, tanpa desimal.
  return "Rp" + n.toLocaleString("id-ID", { maximumFractionDigits: 0 });
}

export function parseIDR(input) {
  if (typeof input === "number") return input;
  if (!input) return 0;
  const digits = String(input).replace(/[^\d]/g, "");
  return digits ? parseInt(digits, 10) : 0;
}

/** Jumlah barang (A6): pemisah ribuan id-ID (+ koma desimal bila perlu),
 *  satuan opsional: formatQty(150, "pcs") → "150 pcs". */
export function formatQty(n, unit) {
  if (n === null || n === undefined || n === "") return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return "—";
  const s = num.toLocaleString("id-ID", { maximumFractionDigits: 2 });
  return unit ? `${s} ${unit}` : s;
}

/** Persentase (A6): tanpa desimal kecuali perlu. formatPercent(10.5) → "10,5%".
 *  Input berupa angka persen (10.5), BUKAN rasio (0.105). */
export function formatPercent(n) {
  if (n === null || n === undefined || n === "") return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return "—";
  return num.toLocaleString("id-ID", { maximumFractionDigits: 1 }) + "%";
}

// --- internal: ambil komponen tanggal-jam sebuah instan di zona tertentu ---
function _parts(dt, tzName) {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    ...(tzName ? { timeZone: tzName } : {}),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const out = {};
  for (const p of fmt.formatToParts(dt)) out[p.type] = p.value;
  return out;
}

/** Label zona untuk IANA name: WIB/WITA/WIT, selain itu UTC±HH:MM (pasti,
 *  bukan tebakan — cermin _TZ_LABELS backend pdf_service). */
export function tzLabel(tzName) {
  if (!tzName) return "";
  if (TZ_LABELS[tzName]) return TZ_LABELS[tzName];
  try {
    const p = new Intl.DateTimeFormat("en-GB", {
      timeZone: tzName,
      timeZoneName: "longOffset",
    }).formatToParts(new Date());
    const off = p.find((x) => x.type === "timeZoneName")?.value || "";
    return off.replace("GMT", "UTC");
  } catch {
    return "";
  }
}

/** Tanggal (A6): "05 Agu 2026". `tzName` opsional — tanpa itu memakai zona
 *  perangkat (tanggal bisa bergeser lintas zona, jadi kirim tzName kalau ada). */
export function formatDate(d, tzName) {
  if (!d) return "—";
  const dt = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return "—";
  try {
    const p = _parts(dt, tzName);
    const monthIdx = parseInt(p.month, 10) - 1;
    return `${p.day} ${MONTHS_ID_SHORT[monthIdx]} ${p.year}`;
  } catch {
    return "—";
  }
}

/** Tanggal + jam (A6): "05 Agu 2026 14.30 WITA". Tanpa tzName → tanpa label
 *  zona (lihat aturan di header file — tidak menebak). */
export function formatDateTime(d, tzName) {
  if (!d) return "—";
  const dt = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return "—";
  try {
    const p = _parts(dt, tzName);
    const monthIdx = parseInt(p.month, 10) - 1;
    // Intl bisa mengembalikan "24" untuk tengah malam di beberapa engine.
    const hh = p.hour === "24" ? "00" : p.hour;
    const label = tzName ? tzLabel(tzName) : "";
    const base = `${p.day} ${MONTHS_ID_SHORT[monthIdx]} ${p.year} ${hh}.${p.minute}`;
    return label ? `${base} ${label}` : base;
  } catch {
    return "—";
  }
}

export function daysBetween(targetISO) {
  if (!targetISO) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(targetISO);
  target.setHours(0, 0, 0, 0);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

/** Tanggal KALENDER LOKAL perangkat "YYYY-MM-DD" (inventaris C10, MANDAT-TUNTAS-25SEP). `toISOString()` memberi tanggal
 *  UTC — pukul 00.00–07.00 WIB (08.00 WITA) hasilnya KEMARIN, dan tanggal bisnis (bayar, opname, laporan) salah hari. */
export function tanggalLokalISO(d = new Date()) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function todayISO() {
  return tanggalLokalISO(new Date());
}

export function isoPlusDays(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return tanggalLokalISO(d);
}
